import type {Flow} from '@features/db/schema';
import {createContext, useState} from 'react';

export const FlowContext = createContext({
  get flow(): Flow {
    throw new Error('flowContext was not initialized');
  },
});

// `updatedAt` and `createdAt` come back as fresh Date instances on every
// tRPC fetch and `updatedAt` flips on every mutation across the flow.
// No client consumer reads either field, so ignore them when deciding
// whether the wrapper needs to be re-published.
const FLOW_FIELDS_TO_IGNORE = new Set<string>(['updatedAt', 'createdAt']);

function isFlowEquivalent(a: Flow, b: Flow): boolean {
  if (a === b) return true;
  for (const key of Object.keys(a)) {
    if (FLOW_FIELDS_TO_IGNORE.has(key)) continue;
    if (a[key as keyof Flow] !== b[key as keyof Flow]) return false;
  }
  for (const key of Object.keys(b)) {
    if (FLOW_FIELDS_TO_IGNORE.has(key)) continue;
    if (!(key in a)) return false;
  }
  return true;
}

/**
 * Memoize the FlowContext value so identity stays stable across tRPC cache
 * updates that don't actually change any consumed field. Without this every
 * mutation in the flow (which bumps `updatedAt`) broadcasts a useless context
 * update to every per-frame consumer (FrameNode, dropdowns, …) and dominates
 * screen-select cost.
 */
export function useFlowContextValue(
  flow: Flow | null | undefined,
): {flow: Flow} | null {
  const [prevFlow, setPrevFlow] = useState(flow);
  const [wrapper, setWrapper] = useState<{flow: Flow} | null>(
    flow ? {flow} : null,
  );

  if (flow !== prevFlow) {
    setPrevFlow(flow);
    if (!flow) {
      if (wrapper !== null) setWrapper(null);
    } else if (!wrapper || !isFlowEquivalent(wrapper.flow, flow)) {
      setWrapper({flow});
    }
  }

  return wrapper;
}
